import gql from 'graphql-tag';
import { Query, Mutation, QueryRegistry } from 'core';

// import { ADDRESS_FIELDS } from 'apps/addresses/gql';
// import { SHARED_FILE_REVISION } from 'apps/share/gql';

const JOBSPEC_FIELDS = gql`
  fragment JobSpecFields on JobSpec {
    id
    quantity
    leadTime

    build {
      id
      process {
        id
        name
        parentProcess {
          id
          name
        }
      }
      material {
        id
        name
        parentMaterial {
          id
          name
        }
      }
      file {
        id
        filename
      }
      values {
        of {
          id
          valueType
          name
          unit
          nilLabel
          prefix
        }
        valueFloat
        valueInt
        valueBool
        valueString
      }
      # supportingFiles {
      #   id
      #   filename
      #   permalink
      #   fileLastModified
      # }
    }
  }
`;
const JOBSPEC_FIELDS2 = gql`
  fragment JobSpecFields2 on JobSpec {
    id
    quantity
    leadTime
    build {
      id
      process {
        id
        name
        parentProcess {
          id
          name
        }
      }
      material {
        id
        name
        parentMaterial {
          id
          name
        }
      }
      file {
        id
        filename
      }
      values {
        of {
          id
          valueType
          name
          unit
          nilLabel
          prefix
        }
        valueFloat
        valueInt
        valueBool
        valueString
      }
      # supportingFiles {
      #   id
      #   filename
      #   permalink
      #   fileLastModified
      # }
    }
  }
`;

const ORDER_FRAGMENT = gql`
  ${JOBSPEC_FIELDS}
  ${JOBSPEC_FIELDS2}
  fragment orderFields on Order {
    id
    createdAt
    nextPossibleStatuses
    quote {
      id
      totalCost
      bufferTime
      manufacturingTime
      productQuotes {
        id
        finalCost
        currency
        for {
          ...JobSpecFields
        }
        from {
          id
          jobSpec {
            ...JobSpecFields2
          }
        }
      }
      miscItems {
        id
        name
        cost
        discount
        currency
      }
      shippingQuote {
        id
        deliveryTime
        discount
        cost
      }
      customerSpace {
        id
        spaceType
        content {
          id
          slug
          title
        }
      }
      address {
        id
        address1
        city
        company
        country
        name
      }
    }
    paymentStatus
    orderStatus
    confirmation {
      id
      customer
      timestamp
    }
    payments {
      id
      tax
      amount
      currency
      receiptUrl
      tax
      card {
        brand
        name
        last4
        expMonth
        expYear
      }
      billingAddress {
        name
        email
        phone
        city
        country
        line1
        line2
        postalCode
        state
      }
    }
    shippingDetail {
      id
      address {
        id
        address1
        city
        company
        country
        name
      }
    }
  }
`;

const ORDER_QUERY = gql`
  ${ORDER_FRAGMENT}
  query Order($id: ID!) {
    order(id: $id) {
      ...orderFields
    }
  }
`;

export class OrderQuery extends Query {
  static conf = {
    GQL: ORDER_QUERY,
    varNames: ['id'],
    resultPath: 'order',
  };
}
QueryRegistry.add('order.OrderQuery', OrderQuery);

const INTERMEDIATOR_ORDER_QUERY = gql`
  ${ORDER_FRAGMENT}
  query IntermediatorOrder($id: ID!) {
    order(id: $id) {
      ...orderFields
      quote {
        takeRate
        productQuotes {
          cost
        }
      }
    }
  }
`;

export class IntermediatorOrderQuery extends Query {
  static conf = {
    GQL: INTERMEDIATOR_ORDER_QUERY,
    varNames: ['id'],
    resultPath: 'order',
  };
}
QueryRegistry.add('order.OrderQuery', OrderQuery);

/*
   TODO: separate Intermdiator query with customerSpace
   Create CustomerSpace fragment (see rfq/gql.js)
*/

// JOBSPEC_FIELDS/2 is a work around the issue of not being able to reuse a fragment in a query
const ORDER_CONNECTION_FIELDS = gql`
  ${JOBSPEC_FIELDS}
  ${JOBSPEC_FIELDS2}
  fragment orderConnectionFields on Order {
    id
    paymentStatus
    orderStatus
    createdAt
    payments {
      id
      receiptUrl
    }
    quote {
      id
      totalCost
      productQuotes {
        id
        finalCost
        currency
        createdAt
        for {
          ...JobSpecFields
        }
        from {
          id
          jobSpec {
            ...JobSpecFields2
          }
        }
      }
      miscItems {
        id
        name
        cost
        discount
        currency
      }

      shippingQuote {
        id
        deliveryTime
        cost
      }
      customerSpace {
        id
        spaceType
        content {
          id
          slug
          title
        }
      }
    }
  }
`;

const ORDER_CONNECTION_QUERY = gql`
  ${ORDER_CONNECTION_FIELDS}
  query Orders(
    $first: Int
    $offset: Int
    $sortField: String
    $sortOrder: SortOrder
    $filters: [FilterInput!]
    $spaceId: ID
  ) {
    orders(
      first: $first
      offset: $offset
      sortField: $sortField
      sortOrder: $sortOrder
      filters: $filters
      spaceId: $spaceId
    ) {
      total
      nodes {
        ...orderConnectionFields
      }
    }
  }
`;

export class OrderConnectionQuery extends Query {
  static conf = {
    GQL: ORDER_CONNECTION_QUERY,
    resultPath: 'orders',
    varNames: [
      'first',
      'offset',
      'sortField',
      'sortOrder',
      'filters',
      'spaceId',
    ],
  };
}

const INTERMEDIATOR_ORDER_CONNECTION_QUERY = gql`
  ${ORDER_CONNECTION_FIELDS}
  query IntermediatorOrders(
    $first: Int
    $offset: Int
    $sortField: String
    $sortOrder: SortOrder
    $filters: [FilterInput!]
    $spaceId: ID
  ) {
    orders(
      first: $first
      offset: $offset
      sortField: $sortField
      sortOrder: $sortOrder
      filters: $filters
      spaceId: $spaceId
    ) {
      total
      nodes {
        ...orderConnectionFields
      }
    }
  }
`;

export class IntermediatorOrderConnectionQuery extends Query {
  static conf = {
    GQL: INTERMEDIATOR_ORDER_CONNECTION_QUERY,
    resultPath: 'orders',
    varNames: [
      'first',
      'offset',
      'sortField',
      'sortOrder',
      'filters',
      'spaceId',
    ],
  };
}

const UPDATE_ORDER_STATUS = gql`
  mutation UpdateOrderStatus($input: UpdateOrderStatusInput!) {
    updateOrderStatus(input: $input) {
      id
      orderStatus
      nextPossibleStatuses
    }
  }
`;

export class UpdateOrderStatusMutation extends Mutation {
  static conf = {
    GQL: UPDATE_ORDER_STATUS,
    inputName: ['input'],
    resultPath: 'updateOrderStatus',
  };
}
